import React from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Chip,
  Divider,
  Stack,
  useTheme,
  useMediaQuery,
  Paper,
  Grid,
  Link,
  Alert,
} from "@mui/material";
import {
  CheckCircle,
  Groups,
  Receipt,
  CalendarToday,
  ManageAccounts,
  EmojiEvents,
  Email,
  ArrowBack,
  Star,
  EventAvailable,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import Logo from "../../EasyAgility.png";

const BRAND_NAVY = "#00395A";
const BRAND_GRADIENT = "linear-gradient(135deg, #00395A 0%, #1a5a7a 100%)";

const INCLUDED_FEATURES = [
  { icon: <ManageAccounts />, label: "Add and remove club members" },
  { icon: <Groups />, label: "Create and manage training groups" },
  { icon: <CalendarToday />, label: "Schedule training sessions with notes" },
  { icon: <Receipt />, label: "Generate and track monthly invoices" },
  { icon: <EventAvailable />, label: "Venue hire for DIY slot bookings" },
  { icon: <EmojiEvents />, label: "Mobile-friendly portal for members" },
  { icon: <CheckCircle />, label: "Upcoming session view for members" },
];

interface PricingTier {
  name: string;
  price: string;
  period?: string;
  members: string;
  description: string;
  cta: string;
  ctaVariant: "contained" | "outlined";
  highlight: boolean;
  badge?: string;
  custom?: boolean;
}

const TIERS: PricingTier[] = [
  {
    name: "Free",
    price: "£0",
    period: "/month",
    members: "Up to 5 members",
    description:
      "Perfect for small training groups just getting started. Venue Hire requires Starter.",
    cta: "Get started free",
    ctaVariant: "outlined",
    highlight: false,
  },
  {
    name: "Starter",
    price: "£9",
    period: "/month",
    members: "6 – 24 members",
    description: "Great for growing clubs running regular weekly sessions.",
    cta: "Get started",
    ctaVariant: "contained",
    highlight: true,
    badge: "Most popular",
  },
  {
    name: "Club",
    price: "£14",
    period: "/month",
    members: "25 – 49 members",
    description: "For established clubs with multiple training groups.",
    cta: "Get started",
    ctaVariant: "outlined",
    highlight: false,
  },
  {
    name: "Pro",
    price: "£19",
    period: "/month",
    members: "50 – 99 members",
    description: "For large, busy clubs that need everything running smoothly.",
    cta: "Get started",
    ctaVariant: "outlined",
    highlight: false,
  },
  {
    name: "Enterprise",
    price: "Custom",
    members: "100+ members",
    description:
      "Tailored pricing for very large clubs. Get in touch and we'll find something that works.",
    cta: "Contact us",
    ctaVariant: "outlined",
    highlight: false,
    custom: true,
  },
];

const PricingPage = (): JSX.Element => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleCta = (tier: PricingTier) => {
    if (tier.custom) {
      window.location.href = "mailto:matt@easyagility.co.uk?subject=Enterprise pricing enquiry";
    } else {
      window.location.href = "/?create-club=1";
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f4f0" }}>
      {/* Top bar */}
      <Box sx={{ bgcolor: BRAND_NAVY, backgroundImage: BRAND_GRADIENT, py: 1.5, px: { xs: 2, sm: 4 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, maxWidth: 1100, mx: "auto" }}>
          <Button
            component={RouterLink}
            to="/"
            startIcon={<ArrowBack />}
            sx={{ color: "rgba(255,255,255,0.8)", textTransform: "none", "&:hover": { color: "white" } }}
          >
            Back
          </Button>
          <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
            <Box sx={{ bgcolor: "white", borderRadius: "6px", p: "4px 5px", display: "flex", alignItems: "center" }}>
              <img src={Logo} alt="Easy Agility" style={{ height: 22, width: "auto" }} />
            </Box>
            <Typography variant="subtitle1" sx={{ color: "white", fontWeight: 700, letterSpacing: 0.3 }}>
              Easy Agility
            </Typography>
          </Box>
          <Box sx={{ width: 80 }} /> {/* balance the back button */}
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
        {/* Hero */}
        <Box sx={{ textAlign: "center", mb: { xs: 5, md: 7 } }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              color: BRAND_NAVY,
              fontSize: { xs: "2rem", sm: "2.75rem", md: "3.25rem" },
              lineHeight: 1.15,
              mb: 2,
            }}
          >
            Simple, transparent pricing
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "text.secondary",
              fontWeight: 400,
              maxWidth: 560,
              mx: "auto",
              fontSize: { xs: "1rem", md: "1.2rem" },
              lineHeight: 1.6,
            }}
          >
            Manage your agility club without the admin headache. Pay only for
            what your club needs, and upgrade any time.
          </Typography>
        </Box>

        {/* Pricing cards */}
        <Grid container spacing={2.5} alignItems="stretch" sx={{ mb: { xs: 6, md: 8 } }}>
          {TIERS.map((tier) => (
            <Grid item xs={12} sm={6} lg={isMobile ? 12 : true} key={tier.name}>
              <Paper
                elevation={tier.highlight ? 8 : 1}
                sx={{
                  height: "100%",
                  borderRadius: 3,
                  border: "2px solid",
                  borderColor: tier.highlight ? BRAND_NAVY : "divider",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  overflow: "visible",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: theme.shadows[tier.highlight ? 12 : 4],
                  },
                }}
              >
                {tier.badge && (
                  <Chip
                    icon={<Star sx={{ fontSize: "14px !important", color: "white !important" }} />}
                    label={tier.badge}
                    size="small"
                    sx={{
                      position: "absolute",
                      top: -13,
                      left: "50%",
                      transform: "translateX(-50%)",
                      bgcolor: BRAND_NAVY,
                      color: "white",
                      fontWeight: 700,
                      fontSize: "0.7rem",
                      letterSpacing: 0.5,
                      px: 1,
                      "& .MuiChip-icon": { ml: 0.5 },
                    }}
                  />
                )}

                {/* Card header */}
                <Box
                  sx={{
                    p: 3,
                    pb: 2,
                    borderRadius: "10px 10px 0 0",
                    bgcolor: tier.highlight ? BRAND_NAVY : "transparent",
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      fontWeight: 700,
                      letterSpacing: 1.2,
                      color: tier.highlight ? "rgba(255,255,255,0.7)" : "text.secondary",
                      fontSize: "0.7rem",
                    }}
                  >
                    {tier.name}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, mt: 0.5, mb: 1 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 800,
                        color: tier.highlight ? "white" : BRAND_NAVY,
                        lineHeight: 1,
                        fontSize: { xs: "2.2rem", md: "2.5rem" },
                      }}
                    >
                      {tier.price}
                    </Typography>
                    {tier.period && (
                      <Typography
                        variant="body2"
                        sx={{ color: tier.highlight ? "rgba(255,255,255,0.6)" : "text.secondary", fontWeight: 500 }}
                      >
                        {tier.period}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    icon={<Groups sx={{ fontSize: "14px !important", color: tier.highlight ? `${BRAND_NAVY} !important` : undefined }} />}
                    label={tier.members}
                    size="small"
                    sx={{
                      bgcolor: tier.highlight ? "rgba(255,255,255,0.15)" : `${BRAND_NAVY}12`,
                      color: tier.highlight ? "white" : BRAND_NAVY,
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      border: "none",
                    }}
                  />
                </Box>

                {/* Card body */}
                <Box sx={{ p: 3, pt: 2, flex: 1, display: "flex", flexDirection: "column" }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                    {tier.description}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                      color: "text.secondary",
                    }}
                  >
                    <CheckCircle sx={{ fontSize: 16, color: "success.main" }} />
                    <Typography variant="body2" fontWeight={500}>
                      All features included
                    </Typography>
                  </Box>

                  <Box sx={{ mt: "auto", pt: 2 }}>
                    <Button
                      fullWidth
                      variant={tier.ctaVariant}
                      onClick={() => handleCta(tier)}
                      sx={{
                        borderRadius: 2,
                        py: 1.25,
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        ...(tier.ctaVariant === "contained"
                          ? {
                              bgcolor: tier.highlight ? "white" : BRAND_NAVY,
                              color: tier.highlight ? BRAND_NAVY : "white",
                              "&:hover": {
                                bgcolor: tier.highlight ? "grey.100" : "#1a5a7a",
                              },
                            }
                          : {
                              borderColor: BRAND_NAVY,
                              color: BRAND_NAVY,
                              "&:hover": {
                                bgcolor: `${BRAND_NAVY}0a`,
                                borderColor: BRAND_NAVY,
                              },
                            }),
                      }}
                    >
                      {tier.cta}
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Alert
          severity="info"
          sx={{
            mb: { xs: 5, md: 6 },
            borderRadius: 2,
            bgcolor: `${BRAND_NAVY}08`,
            border: "1px solid",
            borderColor: `${BRAND_NAVY}22`,
            "& .MuiAlert-icon": { color: BRAND_NAVY },
          }}
        >
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            <strong>Venue Hire</strong> lets members and guests book DIY slots online (30, 60, or 90
            minutes). Enabling it automatically places your club on the{" "}
            <strong>Starter tier (£9/month)</strong>, even with fewer than 5 members.
          </Typography>
        </Alert>

        {/* What's included */}
        <Box sx={{ mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: BRAND_NAVY, textAlign: "center", mb: 1 }}
          >
            Everything you need, in every plan
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ textAlign: "center", mb: 4 }}
          >
            No feature is locked behind a higher tier. The only difference is how many members you
            can have — except Venue Hire, which requires Starter.
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            {INCLUDED_FEATURES.map((f) => (
              <Grid item xs={12} sm={6} md={4} key={f.label}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "white",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      bgcolor: `${BRAND_NAVY}12`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      "& .MuiSvgIcon-root": { fontSize: 20, color: BRAND_NAVY },
                    }}
                  >
                    {f.icon}
                  </Box>
                  <Typography variant="body2" fontWeight={500}>
                    {f.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ mb: { xs: 6, md: 8 } }} />

        {/* Show processing CTA */}
        <Box
          sx={{
            borderRadius: 4,
            background: BRAND_GRADIENT,
            p: { xs: 4, md: 6 },
            textAlign: "center",
            mb: 4,
          }}
        >
          <EmojiEvents sx={{ fontSize: 48, color: "rgba(255,255,255,0.7)", mb: 2 }} />
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: "white", mb: 1.5 }}
          >
            Running a show?
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "rgba(255,255,255,0.8)", mb: 3, maxWidth: 520, mx: "auto", lineHeight: 1.7 }}
          >
            Easy Agility also handles show processing — entries, results, ring plans and more.
            Pricing depends on show size, so get in touch for a quote.
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<Email />}
              href="mailto:matt@easyagility.co.uk?subject=Show processing enquiry"
              sx={{
                bgcolor: "white",
                color: BRAND_NAVY,
                fontWeight: 700,
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: "none",
                fontSize: "1rem",
                "&:hover": { bgcolor: "grey.100" },
              }}
            >
              Contact Matt
            </Button>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
              matt@easyagility.co.uk
            </Typography>
          </Stack>
        </Box>

        {/* Footer note */}
        <Typography variant="body2" color="text.secondary" textAlign="center">
          All prices exclude VAT where applicable.{" "}
          <Link
            component={RouterLink}
            to="/privacy-policy"
            underline="hover"
            color="inherit"
          >
            Privacy Policy
          </Link>
        </Typography>
      </Container>
    </Box>
  );
};

export default PricingPage;
